import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
	GenderOrderArray,
	StatusOrderArray,
	STypeOrderArray,
	UrlAllowedWords,
} from '../FilterData';
import {
	getFilterState,
	setFilterQuery,
	toggleSomeFilter,
	updateFilter,
} from '@/stores/slices/filterSlice';
import { setDefaultTitle } from '@/stores/slices/titleSlice';

import { useNavigate } from 'react-router-dom';
import useLadyService from '@/services/LadyService';
import {
	getCountriesData,
	getUserCurrentCity,
	getUserCurrentDistrict,
	setCity,
	setCurrentCity,
	setUserCurrentDistrict,
} from '@/stores/slices/userSlice';
import { buildFilterParams } from '../FilterParameterBuilder';
import { getIsMainPage } from '@/stores/slices/modelsState';
import useCityChange from '@/hooks/useCityChange';
import { store } from '@/stores/store';

export const useUrlParams = () => {
	const { lang } = useLadyService();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const currentCity = useSelector(getUserCurrentCity);
	const currentDistrict = useSelector(getUserCurrentDistrict);
	const updateSourceRef = useRef(null);
	const pendingUrlRef = useRef(null);
	const urlParams = useParams();
	const countriesData = useSelector(getCountriesData);

	const changeCity = useCityChange();

	const [isPopState, setIsPopState] = useState(false);

	const locationHistoryRef = useRef([]);

	const getFilterValue = (value, revers = false) => {
		const mappings = {
			blond: 'blondes',
			couple: 'couple',
			male: 'guys',
			female: 'girls',
			asian: 'asian',
			video: '',
			travel: '',
			gf: '',
			2: 'anal',
		};

		const reverseMappings = {
			blondes: 'blond',
			couple: 'couple',
			guys: 'male',
			girls: 'female',
			anal: '2',
			asian: 'asian',
		};

		if (!revers) {
			return mappings[value] ?? value;
		}

		// Обратное направление
		return reverseMappings[value] ?? value;
	};

	const is_MainPage = useSelector(getIsMainPage);

	const isMainPage = useMemo(() => {
		if (location.pathname.includes('lk')) {
			return false;
		}

		if (urlParams.profileSlug) {
			return false;
		}

		if (urlParams.params || urlParams.city) {
			return true;
		}

		if (location.pathname === `/${urlParams.lang}`) {
			return true;
		}

		if (location.pathname === '/') return true;

		if (urlParams.city && urlParams.lang)
			if (is_MainPage)
				// last one to check
				return true;

		return false;
	}, [is_MainPage, location.pathname, urlParams]);

	const filterState = useSelector(getFilterState);

	const UrlGenderOrderArray = ['guys', 'girls', 'trans', 'couple'];

	const url_city_param = useMemo(() => {
		const [city, district] = (urlParams.city ?? '').split('_');
		return { city, district };
	}, [urlParams.city]);

	// const on_main_page = useMemo(() => {
	// 	return url_city_param.city;
	// }, [url_city_param, isMainPage]);

	const hasUrlParams = useMemo(() => !!urlParams.params, [urlParams]);

	// get a param category
	const getParamCategory = (param) => {
		if (GenderOrderArray.includes(param)) return 'gender';
		if (STypeOrderArray.includes(param)) return 'service_type';
		if (StatusOrderArray.includes(param)) return 'status';
		return 'other';
	};

	const compareFilter = (arrParams, filterToCompare) => {
		const groupedParams = {
			gender: [],
			service_type: [],
			status: [],
		};
		arrParams.forEach((param) => {
			const category = getParamCategory(getFilterValue(param, true));

			groupedParams[category].push(param);
		});

		for (const [category, expectedParams] of Object.entries(
			filterToCompare,
		)) {
			const actualParams = groupedParams[category];

			// Проверяем длину массивов
			if (actualParams.length !== expectedParams.length) {
				return false;
			}

			// Проверяем содержимое (сортируем для надежности)
			const sortedActual = [...actualParams].sort();
			const sortedExpected = [...expectedParams].sort();

			for (let i = 0; i < sortedActual.length; i++) {
				if (sortedActual[i] !== sortedExpected[i]) {
					return false;
				}
			}
		}

		return true;
	};

	// Функция сортировки параметров по заданному порядку
	const sortUrlParams = (paramsArr) => {
		const categorized = {
			gender: [],
			service_type: [],
			status: [],
			other: [],
		};

		// Распределяем параметры по категориям
		paramsArr.forEach((param) => {
			const category = getParamCategory(param);

			categorized[category].push(param);
		});

		// Сортируем каждую категорию по своему порядку
		const sortedGender = UrlGenderOrderArray.filter((g) =>
			categorized.gender.includes(g),
		);
		const sortedServiceType = STypeOrderArray.filter((s) =>
			categorized.service_type.includes(s),
		);
		const sortedStatus = StatusOrderArray.filter((s) =>
			categorized.status.includes(s),
		);

		// Остальные параметры сохраняем в исходном порядке
		const otherParams = categorized.other;

		// Собираем в правильном порядке: gender -> service_type -> status -> other
		return [
			...sortedGender,
			...sortedServiceType,
			...sortedStatus,
			...otherParams,
		];
	};

	// Track last params to avoid redundant dispatches
	const lastParamsRef = useRef(null);
	const lastUrlPath = useRef(null);

	/**
	 * Apply current filter state and navigate to matching URL
	 * This is the ONLY place where filterQuery should be updated based on user action
	 */
	const applyFilterToUrl = useCallback(
		async (filterObj, redirectAnyway) => {
			console.log('=> APPLY FILTER TO URL: ', filterObj);

			// Build query parameters from current filter state
			const { result, urlParams, urlFilters } = buildFilterParams(
				filterObj,
				filterObj?.ranges?.default_state || {},
				'', // Don't use existing params, build from scratch
				UrlAllowedWords,
			);
			// console.log('=> GET URL-PARAMS FROM BUILDER: ', urlParams);
			// console.log('=> GET URL-FILTERS FROM BUILDER: ', urlFilters);

			// Update Redux with new filter query
			// This will trigger useProfilesFetching to fetch API
			// console.log(`=> result set to filterquery: `, result);
			// console.log('=> toggle here with', result);
			// console.log(
			// 	`=> changed the query: `,
			// 	result,
			// 	'basedOn: ',
			// 	filterObj,
			// 	'applying to url: ',
			// 	urlParams,
			// );

			console.log(`=> result: `, result);

			dispatch(setFilterQuery(result));

			// Маппим значения и сортируем по нужному порядку
			const mapped_url_params = urlParams.map((i) => getFilterValue(i));
			// console.log('=> MAPPED PARAMS STRING: ', mapped_url_params);

			const sortedParams = sortUrlParams(mapped_url_params);
			const sortedParamsString = sortedParams.join('-');
			// console.log('=> SORTED PARAMS STRING: ', sortedParamsString);
			// Формируем путь по отсортированным параметрам
			const filterPath = sortedParams.length
				? `/${sortedParamsString}`
				: '/';

			const currentCitySlug =
				filterObj?.sity_slug ??
				url_city_param.city ??
				currentCity?.slug ??
				'czechia';

			const freshDistrict = getUserCurrentDistrict(store.getState());

			const currentDistrictSlug =
				filterObj?.dist_slug ?? freshDistrict?.slug;

			const cityRoute = currentCitySlug
				? currentDistrictSlug
					? `${currentCitySlug}_${currentDistrictSlug}`
					: `${currentCitySlug}`
				: undefined;

			const newUrl =
				lang === 'en' &&
				currentCitySlug === 'czechia' &&
				!sortedParams.length
					? `/`
					: `/${lang}/${cityRoute ?? 'czechia'}${filterPath}`;

			if (
				isMainPage ||
				(redirectAnyway && !location.pathname.includes('/agency/'))
			) {
				console.log(`=> url.params: `, urlParams);
				updateSourceRef.current = 'filters';
				// console.log('=> GOES TO NEW URL BY FILTERS: ', newUrl);
				dispatch(setDefaultTitle(sortedParams));
				navigate(
					newUrl !== '/en/czechia' && newUrl !== '/en/czechia/'
						? newUrl
						: '/',
				);
			} else {
				console.info(
					'not on main page, skip url update on filter apply',
				);
			}
		},
		[
			dispatch,
			navigate,
			lang,
			currentCity?.slug,
			getFilterValue,
			currentDistrict?.slug,
		],
	);

	/**
	 * Get current filter query string (for comparison/validation)
	 */
	const getFilterQueryString = useCallback(() => {
		const { result } = buildFilterParams(
			filterState,
			filterState.ranges?.default_state || {},
			'',
			UrlAllowedWords,
		);
		return result.join('&');
	}, [filterState]);

	useEffect(() => {
		// console.log(
		// 	`=> checking the URL: \n`,
		// 	location.pathname,
		// 	'params: ',
		// 	urlParams,
		// 	'\ncurrentUserCity: ',
		// 	currentCity,
		// 	'\nlang',
		// 	lang,
		// 	// '\nUUID: ',
		// 	// urlParams.uuid,
		// );

		// if (isPopState) {
		// 	// console.log('=> SKIP URL SYNC ON POPSTATE');
		// 	setIsPopState(false);
		// 	return;
		// }

		// console.info('🔍 useUrlParams RUNS on:', location.pathname);

		// Удаляем слеш только если он в самом конце И за ним ничего нет

		if (location.pathname.includes('lk')) {
			lastParamsRef.current = null;
			return;
		}

		if (updateSourceRef.current === 'filters') {
			updateSourceRef.current = null;
			return;
		}

		if (!isMainPage) {
			// console.info('=> not on main page, skip url check');
			return;
		}

		// Guard: Don't dispatch if params haven't actually changed
		if (
			lastParamsRef.current === urlParams.params &&
			lang === urlParams.lang
		) {
			// console.info(
			// 	'=> url params unchanged, skip dispatch',
			// 	lastParamsRef.current,
			// 	urlParams.params,
			// );
			if (
				location.pathname === '/en/czechia' ||
				location.pathname === '/en/czechia/'
			) {
				// console.info('=> url wrong /en/czechia => /');
				navigate('/', { replace: true });
			}
			return;
		}

		if (lastUrlPath.current === location.pathname) {
			if (lang === 'en' || urlParams.lang === lang) {
				// console.info('=> url location unchanged, skip dispatch');
				return;
			}
		}

		if (updateSourceRef.current === 'filters') {
			updateSourceRef.current = null;
			// console.info('=> updated via filter non need to redirect');
			return;
		}

		lastParamsRef.current = urlParams.params;

		const sitySlug = currentCity?.slug
			? currentDistrict?.slug
				? `${currentCity.slug}_${currentDistrict.slug}`
				: currentCity.slug
			: undefined;

		if (!urlParams.params) {
			if (
				location.pathname === '/' &&
				(lang !== 'en' || sitySlug !== 'czechia')
			) {
				const path = `/${lang}/${sitySlug ?? 'czechia'}`;

				if (path !== '/en/czechia') {
					navigate(`${path}`);
				} else {
					navigate('/', { replace: true });
				}
			} else if (
				location.pathname !== '/' &&
				urlParams.lang === 'en' &&
				sitySlug &&
				sitySlug === 'czechia' &&
				url_city_param.city === 'czechia'
			) {
				navigate('/', { replace: true });
			} else if (urlParams.city && urlParams.lang) {
				const path = location.pathname.replace(urlParams.lang, lang);
				if (path === '/en/czechia' || path === '/en/czechia/') {
					navigate('/', { replace: true });
				} else {
					navigate(location.pathname.replace(urlParams.lang, lang));
				}
			} else if (urlParams.lang && !urlParams.city && !currentCity.slug) {
				navigate(`/${urlParams.lang}/czechia`, { replace: true });
			}
		}

		updateSourceRef.current = 'url';

		// Validate params
		if (urlParams.params) {
			const arrParams = urlParams.params.split('-');

			const hasInvalidWord = arrParams.some(
				(word) => !UrlAllowedWords.includes(word),
			);

			if (hasInvalidWord) {
				console.error(
					'hasInvalidWord - ',
					hasInvalidWord,
					location.pathname,
				);
				navigate('/404');

				return;
			}

			// СОРТИРУЕМ ПАРАМЕТРЫ ПО Порядку
			const sortedParams = sortUrlParams(arrParams);
			const sortedParamsString = sortedParams.join('-');

			// if (params === sortedParamsString) {
			// 	console.log('URL matches  order, skip sync');
			// 	return;
			// }

			const { result, url_filters } = buildFilterParams(
				filterState,
				filterState.ranges.default_state,
				sortedParamsString,
				UrlAllowedWords,
			);

			// console.log(
			// 	'=> UPDATE FILTER VIA URLPARAMS Filter: ',
			// 	url_filters,
			// 	'\nQUERY: ',
			// 	result,
			// );

			// console.log('=> toggle here with another', result);
			dispatch(setFilterQuery(result));
			dispatch(setDefaultTitle(sortedParams));
			dispatch(updateFilter(url_filters));

			const targetUrl = `/${urlParams.lang ?? lang ?? 'en'}/${sitySlug ?? urlParams.city ?? 'czechia'}${sortedParamsString ? `/${sortedParamsString}` : ''}`;

			// Специальная логика для anal
			if (sortedParams.includes('anal')) {
				dispatch(toggleSomeFilter({ key: 'services', payload: '2' }));
			}

			if (targetUrl !== location.pathname) {
				pendingUrlRef.current = targetUrl;
			}
		}

		updateSourceRef.current = null;
	}, [
		urlParams.params,
		urlParams.city,
		lang,
		currentCity,
		location.pathname,
	]);

	// separate the navigation from dispatches
	useEffect(() => {
		if (
			pendingUrlRef.current &&
			pendingUrlRef.current !== location.pathname
		) {
			navigate(pendingUrlRef.current, { replace: true });
			pendingUrlRef.current = null;
		}
	}, [navigate]);

	useEffect(() => {
		// обновляем lastUrlPath только когда location реально изменился
		if (lastUrlPath.current !== location.pathname) {
			lastUrlPath.current = location.pathname;
		}
	}, [location.pathname]);

	// popstate listener to detect back/forward navigation and set a flag
	useEffect(() => {
		const handlePopState = () => setIsPopState(true);
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, []);

	// useEffect(() => {
	// 	if (!isPopState) return;

	// 	const citiesData = countriesData[0]?.['cz']?.cities ?? [];
	// 	const code = countriesData[0]?.['cz']?.code;

	// 	console.info(
	// 		'🏙 Popstate detected, sync city/district from URL:',
	// 		url_city_param,
	// 		'location: ',
	// 		location.pathname,
	// 		'citiesData: ',
	// 		citiesData,
	// 	);

	// 	// window.location.reload();

	// 	// Устанавливаем новые город/район на основе url
	// 	if (url_city_param.city) {
	// 		const matchedCity = citiesData.find(
	// 			(c) => c.slug === url_city_param.city,
	// 		);

	// 		console.log(`=> FOUND MATCH: `, matchedCity);
	// 		if (matchedCity) {
	// 			const matchedDistrict =
	// 				matchedCity.districts.find(
	// 					(item) => item?.id === url_city_param.district,
	// 				) ?? null;

	// 			console.log(`=> changeCity: `, matchedCity, matchedDistrict);

	// 			changeCity(matchedCity.slug, matchedDistrict);

	// 			// dispatch(setCity(matchedCity.slug));
	// 			// dispatch(setCurrentCity({ ...matchedCity, code: 'CZ' }));
	// 			// dispatch(setUserCurrentDistrict(matchedDistrict || {}));
	// 			// navigate(location.pathname);
	// 			window.location.reload();
	// 		}
	// 	}

	// 	setIsPopState(false);
	// }, [isPopState, url_city_param, dispatch]);

	// DEBUG
	useEffect(() => {
		const currentPath = location.pathname;
		const prev =
			locationHistoryRef.current[locationHistoryRef.current.length - 1];

		if (
			locationHistoryRef.current.length === 0 ||
			(prev.key !== location.key && prev.pathname !== currentPath)
		) {
			locationHistoryRef.current.push({
				pathname: currentPath,
				key: location.key,
			});
		}
		// console.trace('🚀 ROUTE CHANGE TRIGGERED FROM:');
		// console.group('📜 ROUTE CHANGE');
		// console.log('➡️  PATH:', currentPath);
		// console.log('🔍 SEARCH:', location.search);
		// console.log('🆔 KEY:', location.key); // уникальный ID каждой локации
		// console.table({
		// 	'Stack size': window.history.length,
		// 	'Current path': location.pathname,
		// });
		// console.log('HISTORY: ', locationHistoryRef.current);
		// console.groupEnd();

		if (locationHistoryRef.current.length > 10) {
			locationHistoryRef.current.shift(); // keep last 10 entries
		}
	}, [location.pathname, location.search, location.key]);

	return {
		url_city_param,
		hasUrlParams,
		applyFilterToUrl,
		getFilterQueryString,
		isMainPage,
		getFilterValue,
	};
};
